import type { RequestStatus } from 'features/request/requestSlice';
import type { Filters, SelectedFilters, TableColumn } from 'uikit';
import { IconCopy, IconInfo, Informer, Table, useDebounce } from 'uikit';
import { isNumber } from 'utils';

import cn from 'classnames';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { usePreviousValue } from '../../hooks/usePrevious';
import { ConfirmationDialog } from '../ConfirmationDialog';
import type { ItemAction } from '../DropdownContext';
import { SearchStringFilter } from '../SearchStringFilter';
import { TableLoader } from '../TableLoader';

import { LeftFilter } from './components/LeftFilter';
import { TopFilter } from './components/TopFilter';
import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeCheckNotificationItem,
    ITableLikeTreeContext,
    ITableLikeTreeFilterItem,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
} from './context';
import { TableLikeTreeContext } from './context';
import { useTableTreeColumns } from './hooks/useTableTreeColumns';
import {
    addChild,
    appendChild,
    deleteItem,
    getAllChildren,
    getAllParentsItems,
    getTableTreeRows,
    insertChild,
    replaceItem,
} from './utils';

import Style from './TableLikeTree.module.css';

type TOnDropFunction = (
    source: ITableLikeTreeItem,
    target: ITableLikeTreeItem,
    onExternalProcessSuccess: (
        source: ITableLikeTreeItem,
        target: ITableLikeTreeItem,
        isInsert?: boolean,
        isBefore?: boolean,
    ) => any,
    rows: ITableLikeTreeItem[],
    isInsert?: boolean,
    isBefore?: boolean,
) => any;

type TOnAddChildExternally = (
    addChild: (child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => any,
    parent?: ITableLikeTreeItem,
) => any;

export interface ITableLikeTreeFilterConfig {
    filterItems?: ITableLikeTreeFilterItem[];
    filteringKey?: string;
    filteringFunction?: (item: any, selectedIds: number[], filterItems: ITableLikeTreeFilterItem[]) => boolean;
    title?: string;
    isFictiveFilter?: boolean;
    onFilterSelectionChange?: (selectedIds: number[]) => void;
    onFilterItemCheckChange?: (
        item?:
            | ITableLikeTreeFilterItem
            | { firstSeason: ITableLikeTreeFilterItem | undefined; lastSeason: ITableLikeTreeFilterItem | undefined },
        isChecked?: boolean,
    ) => void;
    disabled?: boolean;
    isReadOnly?: boolean;
}

export interface ITopFilterConfig extends ITableLikeTreeFilterConfig {
    noFirstListSelectionInSecond?: boolean;
    isFirstListDisabled?: boolean;
    firstListItems?: ITableLikeTreeFilterItem[];
    secondListItems?: ITableLikeTreeFilterItem[];
}

interface ITableLikeTree {
    requestStatus?: RequestStatus;
    className?: string;
    items?: ITableLikeTreeItem[];
    hierarchyColumnTitle: string | React.ReactNode;
    hierarchyColumnFilterTitle?: string;
    menuItems?: ItemAction[];
    menuItemsGetter?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => ItemAction[];
    setContextMenuInHierarchyColumn?: boolean;
    isRowCheckAvailable?: boolean;
    showExpandAllSwitchInHierarchyHeader?: boolean;
    additionalColumns?: TableColumn<any>[];
    textEmptyData?: string;
    onDrop?: TOnDropFunction;
    onCopyDrop?: TOnDropFunction;
    informers?: ITableLikeTreeItemNotification[];
    onInformerClose?: (treeItemId: number) => any;
    onInformersClose?: () => any;
    showClearNotifications?: boolean;
    deleteConfirmationText?: string;
    deleteConfirmationTextHeader?: string;
    showCreateNewObjectButton?: boolean;
    onAddChildExternallyCall?: TOnAddChildExternally;
    getAdditionalClassName?: (variables: any) => string;
    leftFilterConfig?: ITableLikeTreeFilterConfig;
    topFilterConfig?: ITopFilterConfig;
    isRootItemUnableToAppend?: boolean;
    isItemMovableOnlyInsideRootItemBrunch?: boolean;
    isItemMovableOnlyInsideParent?: boolean;
    leftFilterCheckedIds?: number[];
    topFilterCheckedIds?: number[];
    itemsCheckedIds?: number[];
    showCheckedOnly?: boolean;
    onItemCheckChange?: (item: ITableLikeTreeItem, isChecked: boolean, checkedIds: number[]) => void;
    areCheckboxesDisabled?: boolean;
    isHeaderCheckboxDisabled?: boolean;
    tableTreeColumnRightSide?: React.ReactNode;
    tableTreeColumnLeftSide?: React.ReactNode;
    areOuterBordersEnabled?: boolean;
    informerMessage?: string;
    isFirstRowInformer?: boolean;
    filters?: Filters<any>;
    onFiltersUpdated?: (filters: SelectedFilters) => void;
    isFilterEnabled?: boolean;
    getTagLabel?: (id: string, name: string, filterValue: any) => string;
    onLeftFilterRowSelected?: (itemId: number) => void;
    leftFilterSelectedId?: number;
    checkConfirmations?: ITableLikeTreeCheckConfirmation[];
    checkBoxesNotifications?: ITableLikeTreeCheckNotificationItem[];
    disabledCheckBoxesItemsIds?: number[];
    onCheckAll?: (isChecked: boolean) => void;
    isDragOperationDisabled?: boolean;
    dragOperationDisabledItemsIds?: number[];
    isLeftFilterMenuColumnVisible?: boolean;
    showCheckedItemsCount?: boolean;
    isDragButtonHidden?: boolean;
    isDragButtonObjHidden?: boolean;
    leftFilterCheckedCountCache?: Record<string, number>;
    menuColumnWidth?: number;
    menuColumnAlign?: 'left' | 'center' | 'right';
    isOrderChangePage?: boolean;
    noMenuColumn?: boolean;
    onShownChange?: (count: number, values: number[]) => void;
    onRowClick?: (row: ITableLikeTreeItem) => void;
    contextRef?: React.MutableRefObject<ITableLikeTreeContext | undefined>;
    deleteWithoutConfirmation?: boolean;
    onExpandAllSwitch?: (isExpanded: boolean) => void;
    onRowsCountChanged?: (count: number) => void;
    isAllMenuDisabledCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => boolean;
    gridControls?: React.ReactNode;
    hidePointerGhost?: boolean;
    expandAllByDefault?: boolean;
}

const defaultLeftFilterCheckedIds: number[] = [];
const defaultTopFilterCheckedIds: number[] = [];
const defaultItemsCheckedIds: number[] = [];

const leftFilterClasses = 'sub-container left-filter-container';
const heightGridControls = 64;

export const TableLikeTree: React.FC<ITableLikeTree> = (props) => {
    const {
        requestStatus,
        className,
        items = [],
        hierarchyColumnTitle,
        menuItems,
        menuItemsGetter,
        setContextMenuInHierarchyColumn,
        isRowCheckAvailable,
        showExpandAllSwitchInHierarchyHeader,
        additionalColumns,
        textEmptyData,
        onDrop,
        informers,
        onInformerClose,
        onInformersClose,
        showClearNotifications,
        deleteConfirmationText = 'Вы уверены, что хотите удалить элемент?',
        deleteConfirmationTextHeader = 'Удаление',
        showCreateNewObjectButton,
        onAddChildExternallyCall,
        leftFilterConfig,
        topFilterConfig,
        isRootItemUnableToAppend,
        isItemMovableOnlyInsideRootItemBrunch,
        isItemMovableOnlyInsideParent,
        leftFilterCheckedIds = defaultLeftFilterCheckedIds,
        topFilterCheckedIds = defaultTopFilterCheckedIds,
        itemsCheckedIds = defaultItemsCheckedIds,
        onItemCheckChange,
        areCheckboxesDisabled,
        isHeaderCheckboxDisabled,
        tableTreeColumnRightSide,
        tableTreeColumnLeftSide,
        getAdditionalClassName,
        areOuterBordersEnabled,
        informerMessage,
        filters,
        onFiltersUpdated,
        isFilterEnabled,
        hierarchyColumnFilterTitle,
        getTagLabel,
        showCheckedOnly,
        onLeftFilterRowSelected,
        leftFilterSelectedId,
        checkConfirmations,
        disabledCheckBoxesItemsIds,
        onCheckAll,
        isDragOperationDisabled = false,
        dragOperationDisabledItemsIds,
        isLeftFilterMenuColumnVisible,
        showCheckedItemsCount,
        isDragButtonHidden,
        isDragButtonObjHidden,
        leftFilterCheckedCountCache,
        isFirstRowInformer,
        menuColumnWidth,
        menuColumnAlign,
        isOrderChangePage,
        noMenuColumn,
        checkBoxesNotifications,
        onShownChange,
        onRowClick,
        contextRef,
        deleteWithoutConfirmation,
        onExpandAllSwitch,
        onRowsCountChanged,
        isAllMenuDisabledCallback,
        gridControls,
        onCopyDrop,
        hidePointerGhost,
        expandAllByDefault = true,
    } = props;

    const {
        filterItems,
        filteringKey,
        filteringFunction,
        isFictiveFilter,
        onFilterSelectionChange,
        onFilterItemCheckChange,
        disabled: leftFilterDisabled,
        title: leftFilterTitle,
    } = leftFilterConfig || {};

    const {
        filterItems: topFilterItems,
        filteringKey: topFilteringKey,
        filteringFunction: topFilteringFunction,
        title: topFilterTitle,
        isFictiveFilter: topIsFictiveFilter,
        onFilterSelectionChange: topOnFilterChange,
        onFilterItemCheckChange: topOnFilterItemCheckChange,
        disabled: topFilterDisabled,
        isReadOnly: topFilterReadOnly,
        noFirstListSelectionInSecond,
        isFirstListDisabled,
        firstListItems,
        secondListItems,
    } = topFilterConfig || {};

    const [rows, setRows] = useState<ITableLikeTreeItem[]>(() => getTableTreeRows(items));
    const [rowsSource, setRowsSource] = useState(items);
    const itemsIds = useMemo(() => rows.map((r: any) => r?.id), [rows]);
    const [checkedIds, setCheckedIds] = useState<number[]>(itemsCheckedIds);
    const [shownIds, setShownIds] = useState<number[]>(() => (expandAllByDefault ? [] : items.map((item) => item.id)));
    const [currentDragged, setCurrentDragged] = useState<ITableLikeTreeItem | null>(null);
    const [isDeleteConfirmModalOpened, setIsDeleteConfirmModalOpened] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ITableLikeTreeItem | null>(null);
    const [checkedLeftFilterIds, setCheckedLeftFilterIds] = useState<number[]>(leftFilterCheckedIds);
    const [checkedTopFilterIds, setCheckedTopFilterIds] = useState<number[]>(topFilterCheckedIds);
    const [varsStyle, setVarsStyle] = useState<React.CSSProperties>();
    const [nameFilterValue, setNameFilterValue] = useState<string>();
    const [areAllExpanded, setAreAllExpanded] = useState(expandAllByDefault);
    const [isPointerDropMode, setIsPointerDropMode] = useState(false);
    const [pointerMode, setPointerMode] = useState<'copy' | 'move'>('copy');
    const [pointerLabel, setPointerLabel] = useState<string>();
    const pointerGhostRef = useRef<HTMLDivElement>(null);

    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onResize = () => {
            if (tableContainerRef.current) {
                const height = tableContainerRef.current.getBoundingClientRect?.()?.height;

                if (isNumber(height)) {
                    setVarsStyle({
                        '--table-tree-height': `${gridControls ? height - heightGridControls : height}px`,
                    } as React.CSSProperties);
                }
            }
        };

        if (tableContainerRef.current) {
            window.addEventListener('resize', onResize);

            setTimeout(() => onResize(), 100);
        }

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [gridControls]);

    if (rowsSource !== items) {
        const nextRows = getTableTreeRows(items);

        setRowsSource(items);
        setRows(nextRows);

        if (!nameFilterValue) {
            setShownIds(expandAllByDefault ? nextRows.map((row) => row.id) : items.map((item) => item.id));
        }
    }

    useEffect(() => {
        onRowsCountChanged?.(rows.length);
    }, [onRowsCountChanged, rows]);

    const startPointerDropMode = useCallback(
        (source: ITableLikeTreeItem, options?: { mode?: 'copy' | 'move'; label?: string }) => {
            setCurrentDragged(source);
            setPointerMode(options?.mode || 'copy');
            setPointerLabel(options?.label || (source as any)?.title || '');
            setIsPointerDropMode(true);
        },
        [],
    );

    const stopPointerDropMode = useCallback(() => {
        setIsPointerDropMode(false);
        setCurrentDragged(null);
        setPointerLabel(undefined);
    }, []);

    useEffect(() => {
        if (!isPointerDropMode) {
            return;
        }
        const onMove = (e: MouseEvent) => {
            if (pointerGhostRef.current) {
                pointerGhostRef.current.style.left = `${e.clientX - 20}px`;
                pointerGhostRef.current.style.top = `${e.clientY - 20}px`;
            }
        };

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                stopPointerDropMode();
            }
        };

        const onClickOutside = (e: MouseEvent) => {
            if (hidePointerGhost) {
                return;
            }

            if (tableContainerRef.current && !tableContainerRef.current.contains(e.target as Node)) {
                stopPointerDropMode();
            }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onClickOutside);

        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('mousedown', onClickOutside);
        };
    }, [isPointerDropMode, stopPointerDropMode, hidePointerGhost]);

    const prevNameFilterValue = usePreviousValue(nameFilterValue);
    const prevRows = usePreviousValue(rows);
    const prevItems = usePreviousValue(items);
    const prevShowCheckedOnly = usePreviousValue(showCheckedOnly);

    useEffect(() => {
        if (prevNameFilterValue === nameFilterValue && rows === prevRows && showCheckedOnly === prevShowCheckedOnly) {
            setShownIds((prevShownIds) => {
                const value = showCheckedOnly ? prevShownIds.filter((i) => checkedIds.includes(i)) : prevShownIds;

                onShownChange?.(value.length, value);

                return value;
            });

            return;
        }

        let newShownIds: number[];

        if (nameFilterValue) {
            newShownIds = rows
                .filter((r) => r.title?.toLowerCase()?.includes(nameFilterValue.toLowerCase()))
                .reduce((acc: ITableLikeTreeItem[], item) => {
                    const parents = getAllParentsItems(item).filter((p) => !acc.includes(p));

                    acc = [...acc, item, ...parents];

                    return acc;
                }, [])
                .map((r) => r.id);
        } else if (!expandAllByDefault) {
            const isLocalRowsChange =
                prevItems === items &&
                prevNameFilterValue === nameFilterValue &&
                prevShowCheckedOnly === showCheckedOnly;

            if (isLocalRowsChange) {
                return;
            }

            newShownIds = items.map((item) => item.id);
        } else {
            newShownIds = rows.map((r) => r?.id);
        }

        const value = showCheckedOnly ? newShownIds.filter((i) => checkedIds.includes(i)) : newShownIds;

        setShownIds(value);
        onShownChange?.(value.length, value);
    }, [
        checkedIds,
        expandAllByDefault,
        items,
        nameFilterValue,
        onShownChange,
        prevItems,
        prevNameFilterValue,
        prevRows,
        prevShowCheckedOnly,
        rows,
        showCheckedOnly,
    ]);

    useEffect(() => {
        if (topOnFilterChange) {
            topOnFilterChange(checkedTopFilterIds);
        }
    }, [checkedTopFilterIds, topOnFilterChange]);

    useEffect(() => {
        if (onFilterSelectionChange) {
            onFilterSelectionChange(checkedLeftFilterIds);
        }
    }, [checkedLeftFilterIds, onFilterSelectionChange, topOnFilterChange]);

    useEffect(() => {
        setCheckedTopFilterIds(topFilterCheckedIds);
    }, [topFilterCheckedIds]);

    useEffect(() => {
        setCheckedLeftFilterIds(leftFilterCheckedIds);
    }, [leftFilterCheckedIds]);

    useEffect(() => {
        setCheckedIds(itemsCheckedIds);
    }, [itemsCheckedIds]);

    useEffect(() => {
        const shownKey = [...shownIds].sort().join(',');
        const rootKey = items
            .filter((item) => !showCheckedOnly || checkedIds.includes(item.id))
            .map((item) => item.id)
            .sort()
            .join(',');
        const isCollapsed = shownKey === rootKey;

        setAreAllExpanded(!isCollapsed);
        onExpandAllSwitch?.(!isCollapsed);
    }, [checkedIds, items, onExpandAllSwitch, showCheckedOnly, shownIds]);

    const onCheckChange = useCallback(
        (item: ITableLikeTreeItem, isChecked: boolean) => {
            const { id } = item;

            if (isChecked && !checkedIds.includes(id)) {
                const checked = [...checkedIds, id];

                setCheckedIds(checked);
                onItemCheckChange?.(item, isChecked, checked);
            } else {
                const checked = checkedIds.filter((i: number) => i !== id);

                setCheckedIds(checked);
                onItemCheckChange?.(item, isChecked, checked);
            }
        },
        [checkedIds, onItemCheckChange],
    );

    const switchCheckAll = useCallback(
        (checkAll: boolean) => {
            setCheckedIds(checkAll ? itemsIds : []);
            onCheckAll?.(checkAll);
        },
        [itemsIds, onCheckAll],
    );

    const switchExpandAll = useCallback(
        (expandAll: boolean) => {
            const newShownIds = expandAll ? itemsIds : items.map((item) => item.id);

            const value = showCheckedOnly ? newShownIds.filter((i) => checkedIds.includes(i)) : newShownIds;

            setShownIds(value);
            onShownChange?.(value.length, value);
        },
        [checkedIds, items, itemsIds, onShownChange, showCheckedOnly],
    );

    const hideItems = useCallback(
        (ids: number[]) => {
            const hiddenIds = new Set(ids);
            const value = shownIds.filter((id) => !hiddenIds.has(id));

            setShownIds(value);
            onShownChange?.(value.length, value);
        },
        [onShownChange, shownIds],
    );

    const showItems = useCallback(
        (ids: number[]) => {
            const value = [...shownIds, ...ids];

            setShownIds(value);
            onShownChange?.(value.length, value);
        },
        [onShownChange, shownIds],
    );

    const onDragStart = useCallback((item: ITableLikeTreeItem) => {
        setCurrentDragged(item);
    }, []);

    const performDrop = useCallback(
        (source: ITableLikeTreeItem, target: ITableLikeTreeItem, isInsert?: boolean, isBefore?: boolean) => {
            const isTargetInSourceBranch =
                source.id === target.id || getAllChildren(source).some((child) => child.id === target.id);

            if (isTargetInSourceBranch) {
                return;
            }

            const newRows = isInsert ? insertChild(rows, source, target, isBefore) : appendChild(rows, source, target);

            setRows(newRows);
        },
        [rows],
    );

    const onDropLocal = useCallback(
        (source: ITableLikeTreeItem, target: ITableLikeTreeItem, isInsert?: boolean, isBefore?: boolean) => {
            const isCopyDrop = isPointerDropMode && pointerMode === 'copy' && onCopyDrop;
            const isTargetInSourceBranch =
                source.id === target.id || getAllChildren(source).some((child) => child.id === target.id);

            if (!isCopyDrop && isTargetInSourceBranch) {
                if (isPointerDropMode) {
                    stopPointerDropMode();
                }

                return;
            }

            const route = isCopyDrop ? onCopyDrop : onDrop;

            if (route) {
                route(source, target, performDrop, rows, isInsert, isBefore);
            } else {
                performDrop(source, target, isInsert, isBefore);
            }

            if (isPointerDropMode && !isCopyDrop) {
                stopPointerDropMode();
            }
        },
        [onCopyDrop, onDrop, performDrop, rows, stopPointerDropMode, isPointerDropMode, pointerMode],
    );

    const onDelete = useCallback(
        (item: ITableLikeTreeItem) => {
            if (deleteWithoutConfirmation) {
                const newRows = deleteItem(rows, item);

                setRows(newRows);
            } else {
                setIsDeleteConfirmModalOpened(true);
                setItemToDelete(item);
            }
        },
        [deleteWithoutConfirmation, rows],
    );

    const onDeleteConfirmationClose = useCallback(() => {
        setIsDeleteConfirmModalOpened(false);
        setItemToDelete(null);
    }, []);

    const performDelete = useCallback(() => {
        if (itemToDelete) {
            setRows(deleteItem(rows, itemToDelete));
        }

        onDeleteConfirmationClose();
    }, [itemToDelete, onDeleteConfirmationClose, rows]);

    const debouncedOnDrop = useDebounce(onDropLocal, 50);

    const onReplace = useCallback(
        (item: ITableLikeTreeItem) => {
            setRows(replaceItem(rows, item, { ...item }));
        },
        [rows],
    );

    const onAddChild = useCallback(
        (child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => {
            const newRows = addChild(rows, child, parent);

            const value = newRows.map((r: any) => r?.id);

            setRows(newRows);
            setShownIds(value);
            onShownChange?.(value.length, value);
        },
        [onShownChange, rows],
    );

    const onLeftFilterCheckChange = useCallback(
        (ids: number[], item?: ITableLikeTreeFilterItem, isChecked?: boolean) => {
            setCheckedLeftFilterIds(ids);
            onFilterItemCheckChange?.(item, isChecked);
        },
        [onFilterItemCheckChange],
    );

    const onTopFilterCheckChange = useCallback(
        (seasonValues: {
            firstSeason: ITableLikeTreeFilterItem | undefined;
            lastSeason: ITableLikeTreeFilterItem | undefined;
        }) => {
            setCheckedTopFilterIds(
                topFilterItems
                    ?.filter(
                        (item) => item.id === seasonValues?.firstSeason?.id || item.id === seasonValues?.lastSeason?.id,
                    )
                    .map((item) => item.id) ?? [],
            );
            topOnFilterItemCheckChange?.(seasonValues);
        },
        [topFilterItems, topOnFilterItemCheckChange],
    );

    const getClassName = useCallback(
        (data: any) =>
            `${data?.row?.isMessageItem ? 'message-row ' : ''}${getAdditionalClassName?.(data) || ''} treeLevel_${data
                ?.row?.treeLevel} ${data?.row?.treeLevel > 2 ? 'treeLevelMoreThanSecond' : ''}`,
        [getAdditionalClassName],
    );

    const columns = useTableTreeColumns({
        isContextMenuAvailable: !!menuItems?.length || !!menuItemsGetter,
        isRowCheckAvailable,
        menuItems,
        setContextMenuInHierarchyColumn,
        hierarchyColumnTitle,
        showExpandAllSwitchInHierarchyHeader,
        additionalColumns,
        showClearNotifications,
        showCreateNewObjectButton,
        onAddChildExternallyCall,
        isHeaderCheckboxDisabled,
        tableTreeColumnLeftSide,
        tableTreeColumnRightSide,
        menuItemsGetter,
        isDragOperationDisabled,
        isDragButtonHidden,
        isDragButtonObjHidden,
        menuColumnWidth,
        menuColumnAlign,
        noMenuColumn,
        isAllMenuDisabledCallback,
    });

    const currentFilters = useMemo(
        () => [
            ...(isFilterEnabled
                ? [
                      {
                          id: 'name',
                          field: 'name',
                          name: hierarchyColumnFilterTitle || '',
                          filterer: R.T,
                          component: { name: SearchStringFilter },
                      },
                  ]
                : []),
            ...(filters || []),
        ],
        [filters, hierarchyColumnFilterTitle, isFilterEnabled],
    );

    const topFilteredRows = useMemo(() => {
        if (!topIsFictiveFilter && checkedTopFilterIds.length) {
            return rows.filter((row) => {
                if (topFilteringFunction) {
                    return topFilteringFunction(row, checkedTopFilterIds, topFilterItems || []);
                }

                return checkedTopFilterIds.includes((row as any)[topFilteringKey || 'id']);
            });
        }

        return rows;
    }, [checkedTopFilterIds, rows, topFilterItems, topFilteringFunction, topFilteringKey, topIsFictiveFilter]);

    const leftFilteredRows = useMemo(() => {
        if (!isFictiveFilter && checkedLeftFilterIds.length) {
            return topFilteredRows.filter((row) => {
                if (filteringFunction) {
                    return filteringFunction(row, checkedLeftFilterIds, filterItems || []);
                }

                return checkedLeftFilterIds.includes((row as any)[filteringKey || 'id']);
            });
        }

        return topFilteredRows;
    }, [isFictiveFilter, checkedLeftFilterIds, topFilteredRows, filteringFunction, filteringKey, filterItems]);

    const shownIdsSet = useMemo(() => new Set(shownIds), [shownIds]);

    const rowsToShow = useMemo(
        () => leftFilteredRows.filter((row) => shownIdsSet.has(row?.id)),
        [leftFilteredRows, shownIdsSet],
    );

    const onFiltersUpdatedLocal = useCallback(
        (filter: SelectedFilters) => {
            setNameFilterValue(filter?.name?.value);
            onFiltersUpdated?.(filter);
        },
        [onFiltersUpdated],
    );

    const providerObject = useMemo(
        () => ({
            onCheckChange,
            checkedIds,
            onDragStart,
            switchCheckAll,
            rowsCount: items.length,
            shownIds,
            shownIdsSet,
            switchExpandAll,
            hideItems,
            showItems,
            rootLevelItems: items,
            allItems: rows,
            currentDragged,
            onDrop: isPointerDropMode ? onDropLocal : debouncedOnDrop,
            informers,
            onInformerClose,
            onInformersClose,
            delete: onDelete,
            replace: onReplace,
            addChild: onAddChild,
            isRootItemUnableToAppend,
            isItemMovableOnlyInsideRootItemBrunch,
            isItemMovableOnlyInsideParent,
            areCheckboxesDisabled,
            informerMessage,
            checkConfirmations,
            disabledCheckBoxesItemsIds,
            dragOperationDisabledItemsIds,
            checkBoxesNotifications,
            areAllExpanded,
            startPointerDropMode,
            stopPointerDropMode,
            isPointerDropMode,
            pointerMode,
        }),
        [
            onCheckChange,
            checkedIds,
            onDragStart,
            switchCheckAll,
            items,
            shownIds,
            shownIdsSet,
            switchExpandAll,
            hideItems,
            showItems,
            rows,
            currentDragged,
            debouncedOnDrop,
            informers,
            onInformerClose,
            onInformersClose,
            onDelete,
            onReplace,
            onAddChild,
            isRootItemUnableToAppend,
            isItemMovableOnlyInsideRootItemBrunch,
            isItemMovableOnlyInsideParent,
            areCheckboxesDisabled,
            informerMessage,
            checkConfirmations,
            disabledCheckBoxesItemsIds,
            dragOperationDisabledItemsIds,
            checkBoxesNotifications,
            areAllExpanded,
            startPointerDropMode,
            stopPointerDropMode,
            isPointerDropMode,
            pointerMode,
            onDropLocal,
        ],
    );

    useImperativeHandle(contextRef, () => providerObject);

    const filterContainersClasses = useMemo(
        () => (areOuterBordersEnabled ? `outer-bordered ${leftFilterClasses}` : leftFilterClasses),
        [areOuterBordersEnabled],
    );

    const tablesContainersClasses = useMemo(
        () => `sub-container ${informerMessage && isFirstRowInformer ? 'first-row-informer' : ''}`,
        [informerMessage, isFirstRowInformer],
    );

    const getTagLabelLocal = useCallback(
        (id: string, name: string, filterValue: any) => {
            if (getTagLabel) {
                return getTagLabel?.(id, name, filterValue);
            }

            return `${name}: '${filterValue || ''}'`;
        },
        [getTagLabel],
    );

    return (
        <div className={`${Style.tableLikeTree} ${className}`} onClickCapture={() => onRowClick?.({} as any)}>
            {topFilterConfig && (
                <TopFilter
                    checkedIds={checkedTopFilterIds}
                    onCheckChange={onTopFilterCheckChange}
                    items={topFilterItems}
                    title={topFilterTitle}
                    disabled={topFilterDisabled}
                    isOrderChangePage={isOrderChangePage}
                    isReadOnly={topFilterReadOnly}
                    noFirstListSelectionInSecond={noFirstListSelectionInSecond}
                    isFirstListDisabled={isFirstListDisabled}
                    firstListItems={firstListItems}
                    secondListItems={secondListItems}
                />
            )}
            <div className={cn({ [Style.tableLikeTreeContainer]: leftFilterConfig })} ref={tableContainerRef}>
                {leftFilterConfig && (
                    <div className={filterContainersClasses} style={varsStyle}>
                        <LeftFilter
                            items={filterItems}
                            onCheckChange={onLeftFilterCheckChange}
                            checkedIds={checkedLeftFilterIds}
                            disabled={leftFilterDisabled}
                            title={leftFilterTitle}
                            onRowSelected={onLeftFilterRowSelected}
                            selectedId={leftFilterSelectedId}
                            isMenuColumnVisible={isLeftFilterMenuColumnVisible}
                            mainCheckedIds={checkedIds}
                            showCheckedMainItems={showCheckedItemsCount}
                            checkedCountCache={leftFilterCheckedCountCache}
                        />
                    </div>
                )}
                <div className={tablesContainersClasses} style={varsStyle}>
                    {informerMessage && (
                        <Informer
                            className={isFirstRowInformer ? Style.headerInformer : Style.aboveInformer}
                            status="system"
                            view="filled"
                            label={
                                <div>
                                    <IconInfo size="s" />
                                    {informerMessage}
                                </div>
                            }
                        />
                    )}
                    <TableLikeTreeContext.Provider value={providerObject}>
                        <div className={Style.wrapperBorderedRightFilter}>
                            {gridControls}
                            <Table
                                columns={columns}
                                rows={rowsToShow}
                                size="s"
                                stickyHeader
                                emptyRowsPlaceholder={
                                    <TableLoader requestStatus={requestStatus} textEmptyData={textEmptyData} />
                                }
                                borderBetweenColumns
                                verticalAlign="center"
                                getAdditionalClassName={getClassName}
                                filters={currentFilters}
                                onFiltersUpdated={onFiltersUpdatedLocal}
                                getTagLabel={getTagLabelLocal}
                                className={areOuterBordersEnabled ? 'outer-bordered ' : ''}
                                onRowClick={
                                    onRowClick
                                        ? ({ id }) => {
                                              const row = rowsToShow.find((r) => String(r.id) === id);

                                              if (row) {
                                                  onRowClick(row);
                                              }
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </TableLikeTreeContext.Provider>
                </div>
            </div>

            {isPointerDropMode && pointerLabel && (
                <div
                    ref={pointerGhostRef}
                    className={Style.pointerGhost}
                    style={{ display: hidePointerGhost ? 'none' : 'flex' }}
                >
                    <IconCopy color="#00203399" size="s" />
                    {pointerLabel}
                </div>
            )}

            {isDeleteConfirmModalOpened && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={performDelete}
                    onClose={onDeleteConfirmationClose}
                    textHeader={deleteConfirmationTextHeader}
                    textConfirmation={deleteConfirmationText}
                    confirmButtonText="Удалить"
                    cancelButtonText="Отменить"
                />
            )}
        </div>
    );
};

TableLikeTree.displayName = 'TableLikeTree';
